// components/ui/index.jsx
// ─────────────────────────────────────────────────────────────
// All small, reusable, stateless UI primitives in one file.
// Import individually:
//   import { Modal, Spinner, Pagination } from "@/components/ui";
// ─────────────────────────────────────────────────────────────
import { useEffect } from "react";

// ── Spinner ───────────────────────────────────────────────────
export function Spinner({ large = false }) {
  return <div className={`spinner${large ? " spinner-lg" : ""}`} />;
}

// ── Loading block ─────────────────────────────────────────────
export function Loading() {
  return (
    <div className="loading-block">
      <Spinner large />
      <span>Loading…</span>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────
export function Pagination({ page, total, setPage, count, perPage = 8 }) {
  if (total <= 1) return null;

  const start = (page - 1) * perPage + 1;
  const end   = Math.min(page * perPage, count ?? 0);
  const pages = Array.from({ length: total }, (_, i) => i + 1);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 16, flexWrap: "wrap", gap: 8 }}>
      {count != null && (
        <span className="pagination-info">Showing {start}–{end} of {count}</span>
      )}
      <div className="pagination">
        <div className={`page-btn${page === 1 ? " disabled" : ""}`} onClick={() => page > 1 && setPage(page - 1)}>‹</div>
        {pages.map((p) => (
          <div key={p} className={`page-btn${p === page ? " active" : ""}`} onClick={() => setPage(p)}>{p}</div>
        ))}
        <div className={`page-btn${page === total ? " disabled" : ""}`} onClick={() => page < total && setPage(page + 1)}>›</div>
      </div>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────
export function Modal({ open, onClose, title, subtitle, children, footer, large, xl }) {
  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && open) onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${large ? " modal-lg" : ""}${xl ? " modal-xl" : ""}`}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{title}</h2>
            {subtitle && <div className="modal-subtitle">{subtitle}</div>}
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ── Confirm / Delete modal ────────────────────────────────────
export function ConfirmModal({ open, onClose, onConfirm, title, message, loading, confirmLabel = "Delete", danger = true }) {
  return (
    <Modal
      open={open} onClose={onClose} title={title}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onConfirm} disabled={loading}>
            {loading ? <Spinner /> : confirmLabel}
          </button>
        </>
      }
    >
      <p style={{ color: "var(--text-muted)", fontSize: 13.5, lineHeight: 1.6 }}>{message}</p>
    </Modal>
  );
}

// ── Breadcrumb ────────────────────────────────────────────────
export function Breadcrumb({ items }) {
  return (
    <nav className="breadcrumb">
      {items.map((item, i) => (
        <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {i > 0 && <span className="breadcrumb-sep">›</span>}
          <span
            className={`breadcrumb-item${i === items.length - 1 ? " active" : ""}`}
            onClick={item.onClick}
          >
            {item.label}
          </span>
        </span>
      ))}
    </nav>
  );
}

// ── Empty state ───────────────────────────────────────────────
export function EmptyState({ icon = "📭", title, subtitle, action }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <div className="empty-title">{title}</div>
      {subtitle && <div className="empty-sub">{subtitle}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}
